import { useEffect } from 'react';
import { X } from 'lucide-react';
import Button from './Button';
import { useDraggableResizable, ResizeHandles } from '../../hooks/useDraggableResizable';

/**
 * Modal - Reusable modal component for forms and content
 * Features: Draggable header, resizable from corners and edges
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal is closed
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.icon - Icon to display in header
 * @param {React.ReactNode} props.children - Modal content
 * @param {React.ReactNode} props.footer - Custom footer content (overrides default buttons)
 * @param {string} props.submitText - Text for submit button
 * @param {string} props.cancelText - Text for cancel button
 * @param {Function} props.onSubmit - Called when submit button is clicked
 * @param {boolean} props.loading - Show loading state on submit button
 * @param {boolean} props.hideFooter - Hide the footer entirely
 * @param {boolean} props.hideCancel - Hide cancel button
 * @param {'sm'|'md'|'lg'|'xl'|'full'} props.size - Modal size (affects initial dimensions)
 * @param {boolean} props.closeOnOverlayClick - Close modal when clicking overlay (default: true)
 * @param {boolean} props.resizable - Enable resize handles (default: true)
 * @param {boolean} props.draggable - Enable dragging (default: true)
 */
const Modal = ({
    isOpen,
    onClose,
    title,
    icon: Icon,
    children,
    footer,
    submitText = 'Enregistrer',
    cancelText = 'Annuler',
    onSubmit,
    loading = false,
    hideFooter = false,
    hideCancel = false,
    size = 'md',
    closeOnOverlayClick = true,
    resizable = true,
    draggable = true
}) => {
    // Size presets for initial dimensions (height as 90% of viewport by default)
    const sizePresets = {
        sm: { width: 400, height: Math.min(350, window.innerHeight * 0.9) },
        md: { width: 550, height: Math.min(500, window.innerHeight * 0.9) },
        lg: { width: 750, height: window.innerHeight * 0.9 },
        xl: { width: 950, height: window.innerHeight * 0.9 },
        full: { width: window.innerWidth - 64, height: window.innerHeight - 64 }
    };

    const preset = sizePresets[size] || sizePresets.md;

    const {
        modalRef,
        modalStyle,
        isDragging,
        handleDragStart,
        handleResizeStart,
        initializeModal,
        reset,
    } = useDraggableResizable({
        minWidth: 350,
        minHeight: 250,
        initialWidth: preset.width,
        initialHeight: preset.height,
    });

    // Initialize modal position and size when opened
    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => {
                initializeModal();
            });
        } else {
            reset();
        }
    }, [isOpen, initializeModal, reset]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[10000] overflow-hidden">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/30 transition-opacity"
                onClick={handleOverlayClick}
            />

            {/* Modal container */}
            <div className="flex min-h-full items-center justify-center p-4 pointer-events-none">
                <div
                    ref={modalRef}
                    style={modalStyle}
                    className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col pointer-events-auto ${
                        !modalStyle.width ? 'w-full max-w-lg' : ''
                    }`}
                >
                    {/* Resize handles */}
                    {resizable && <ResizeHandles onResizeStart={handleResizeStart} />}

                    {/* Header - Draggable */}
                    <div
                        className={`bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-4 sm:p-5 text-white rounded-t-xl select-none ${
                            draggable ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
                        }`}
                        onMouseDown={draggable ? handleDragStart : undefined}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {Icon && (
                                    <div className="p-2 bg-white/20 rounded-full">
                                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                                    </div>
                                )}
                                <h2 className="text-lg sm:text-xl font-bold">{title}</h2>
                            </div>
                            <button
                                onClick={handleClose}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                aria-label="Fermer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content - scrollable */}
                    <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                        {children}
                    </div>

                    {/* Footer */}
                    {!hideFooter && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900 rounded-b-xl">
                            {footer ? (
                                footer
                            ) : (
                                <>
                                    {!hideCancel && (
                                        <Button
                                            variant="ghost"
                                            onClick={handleClose}
                                        >
                                            {cancelText}
                                        </Button>
                                    )}
                                    {onSubmit && (
                                        <Button
                                            variant="primary"
                                            onClick={onSubmit}
                                            loading={loading}
                                        >
                                            {submitText}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Modal;
